import {
  AvatarStyleE,
  HeaderEffectE,
  PatternE,
  RoundedE,
  SocialDisplayStyleE,
  SubtitleTypeFontE,
  TemplateCategoryE,
  TitleTypeFontE,
  TypeButtonsE
} from '@/containers/StoreFrontBuilder/context/storefront.enums';

export type ScheduleStatusT = 'bookable' | 'paused' | 'canceled' | 'finished' | 'full';

export interface UserI {
  id: number;
  auth_id: string;
  first_name?: string;
  last_name?: string;
  nickname?: string;
  connection_type: string;
  email: string;
  phone?: string;
  dob?: string;
  avatar_url?: string;
  created_at: string;
  updated_at?: string;
  vendor?: VendorI;
  google_calendar_keys?: GoogleCalendarKeyI;
  vendor_clients_base?: VendorClientsBaseI[];
  bookings?: BookingI[];
  is_experimental: boolean;
}

export interface NotificationI {
  id: number;
  email_messages: boolean;
  sms_messages: boolean;
  whatsapp_messages: boolean;
  email_reminders: boolean;
  sms_reminders: boolean;
  whatsapp_reminders: boolean;
}

export type ClientAutoTagE = 'nuevo' | 'frecuente' | 'en_riesgo' | 'ausente';

export type ClientSegmentKey =
  'all' | 'nuevo' | 'frecuente' | 'en_riesgo' | 'ausente' | 'adeuda' | 'cumpleanos' | 'sin_historial';

export interface ClientSegmentCounts {
  all: number;
  nuevo: number;
  frecuente: number;
  en_riesgo: number;
  ausente: number;
  adeuda: number;
  cumpleanos: number;
  sin_historial: number;
}

export interface VendorClientCriteriaI {
  id: number;
  vendor_id: number;
  nuevo_enabled: boolean;
  nuevo_window_days: number;
  nuevo_max_bookings: number;
  frecuente_enabled: boolean;
  frecuente_window_days: number;
  frecuente_min_bookings: number;
  en_riesgo_enabled: boolean;
  en_riesgo_min_history: number;
  en_riesgo_factor: number;
  en_riesgo_floor_days: number;
  ausente_enabled: boolean;
  ausente_days: number;
  ausente_min_history: number;
  /** Segmentos sugeridos que el negocio eliminó de su panel (vacío = todos visibles). */
  hidden_segments?: string[];
  /** Orden de la lista de segmentos: `c:<id>` (propios) y claves de predeterminados. Vacío = el de siempre. */
  segment_order?: string[];
  adeuda_window_days: number;
  cumpleanos_window_days: number;
}

export interface ClientSegmentFiltersI {
  birthdayMonth?: number;
  birthdayFrom?: string;
  birthdayTo?: string;
  lastVisitDaysMin?: number;
  lastVisitDaysMax?: number;
  lastVisitNever?: boolean;
  minBookings?: number;
  maxBookings?: number;
  hasPhone?: boolean;
  hasEmail?: boolean;
  serviceIds?: number[];
  staffVendorIds?: number[];
  firstVisitFrom?: string;
  firstVisitTo?: string;
  lastVisitFrom?: string;
  lastVisitTo?: string;
  neverVisited?: boolean;
  birthdayMonthOffset?: number;
  premio?: 'listo' | 'cerca';
}

export interface VendorClientSegmentI {
  id: number;
  vendor_id: number;
  name: string;
  position: number;
  client_tag_ids?: number[];
  tags?: ClientTagI[];
  client_count?: number;
  filters?: ClientSegmentFiltersI | null;
}

export interface VendorClientsBaseI {
  id: number;
  user_email: string;
  user_phone?: string | null;
  address?: string;
  discount: number;
  vendor_id: number;
  user_id: number;
  notes?: string;
  created_at: Date;
  updated_at?: Date;
  vendor: VendorI;
  user?: UserI;
  vendor_clients_base_history?: VendorClientsBaseHistoryI[];
  tags?: ClientTagI[];
  // Computed fields for clients table
  first_visit?: Date | null;
  last_visit?: Date | null;
  total_visits?: number;
  total_cancellations?: number;
  total_revenue?: number;
  dob?: string | null;
  // Auto-derived (computed by backend per vendor's criteria)
  auto_tag?: ClientAutoTagE | null;
  /** Premios: los puntos que le quedan para usar. null si el negocio no tiene Premios activo. */
  premios_points?: number | null;
  is_birthday_window?: boolean;
  is_in_debt?: boolean;
  has_no_history?: boolean;
  /** Reservas online restringidas por el negocio. Sólo viaja si el negocio tiene la beta. */
  booking_restricted?: boolean;
  debt_amount?: number;
  // "Una sola verdad": números de la ficha viva para la fila (visitas asistidas,
  // gasto neto en la moneda dominante, ritmo por mediana). Solo viene en la página
  // del endpoint paginado; si falta, la fila cae a los agregados legacy de arriba.
  live_stats?: {
    visits: number;
    first_visit: string | null;
    last_visit: string | null;
    next_visit: string | null;
    cadence_days: number | null;
    spend_total: number;
    spend_currency: string | null;
  } | null;
}

export interface VendorClientsBaseHistoryI {
  id: number;
  title: string;
  description: string;
  vendor_id: number;
  vendor_clients_base_id: number;
  created_at: Date;
  updated_at?: Date;
  vendor: VendorI;
  vendor_clients_base: VendorClientsBaseI;
}

export interface ClientTagI {
  id: number;
  name: string;
  color: string;
  vendor_id?: number;
  is_default: boolean;
  created_at: Date;
  updated_at?: Date;
}

export interface VendorGooglePlaceI {
  place_id: string;
  name?: string | null;
  address?: string | null;
  photo_url?: string;
  /** PostgreSQL numeric values can arrive as strings in the vendor response. */
  rating?: number | string | null;
  review_count?: number | null;
  maps_url?: string | null;
  write_review_url?: string | null;
}

export interface VendorI {
  vendor_google_place?: VendorGooglePlaceI | null;
  is_wa_reminder_enabled?: boolean;
  product_catalog_visible?: boolean;
  booking_hold_minutes?: number;
  show_booking_countdown?: boolean;
  city?: string;
  id: number;
  plan_name?: string;
  username: string;
  address: string;
  address_details?: string;
  latitude: string;
  longitude: string;
  neighborhood: string;
  country: SupportedCountriesE;
  timezone: string;
  state: string;
  bio: string;
  professional_bio: string;
  professional_title: string;
  facebook_pixel_id?: string | null;
  google_tag_id?: string | null;
  clarity_project_id?: string | null;
  cover_url: string;
  policy_refund_days: number;
  is_created_by_marketplace: boolean;
  is_onboarding_finished: boolean;
  onboarding_finished_at: string;
  is_payment_cash_enabled: boolean;
  is_payment_bank_transfer_enabled: boolean;
  status: VendorStatusE;
  /** El negocio lo sacó de las reservas online: sigue en la agenda, pero no en la página. */
  is_hidden?: boolean;
  vendor_working_hours_start_frequency?: number;
  payment_gateways?: WalletTypeE[];
  created_at: string;
  updated_at?: string;
  user_id?: number;
  user: UserI;
  social_media?: SocialMediaI;
  services?: ServiceI[];
  services_vendors?: ServiceVendorI[];
  subscriptions?: SubscriptionI[];
  digital_contents?: DigitalContentI[];
  certifications?: CertificationI[];
  experiences?: ExperienceI[];
  wallet_mercado_pago?: WalletMercadoPagoI;
  vendor_prompt?: VendorPromptsI;
  offline_payments?: OfflinePaymentI[];
  category_assets?: CategoryAsset[];
  vendor_links?: VendorLinkI[];
  vendor_social_apps?: VendorSocialAppI[];
  vendor_storefront?: VendorStorefrontI;
  vendor_blocked_hours?: VendorBlockedHoursI[];
  vendor_on_demand_hours?: VendorOnDemandHoursI[];
  unique_events?: UniqueEventI[];
  vendor_working_hours?: VendorWorkingHoursI[];
  vendor_agora_plans?: VendorAgoraPlanI[];
  vendor_status_history?: VendorStatusHistoryI[];
  vendor_permissions?: VendorPermissionsI;
  vendor_activity_tracking?: VendorActivityTrackingI;
  vendor_notifications?: VendorNotificationI[];
  number_services_available?: number;
  venue_id?: number;
  venue?: VenueI;
  staff_position?: number | null;

  is_first_setup_completed: boolean;
  is_full_setup_completed: boolean;

  max_booking_period?: number;
  max_booking_fixed_date?: string;
  policy_refund_mins?: number;
  /** Texto de política que ve el cliente en el checkout. Vacío ⇒ frase autogenerada. */
  cancellation_policy_text?: string | null;
  booking_until_at_minutes: number;
  is_user_phone_required: boolean;
  is_user_dob_required: boolean;
  allow_booking_for_others: boolean;
  is_refund_policies_enabled: boolean;
  is_down_payment_enabled: boolean;
  is_down_payment_global: boolean;
  down_payment_amount?: number;
  down_payment_amount_usd?: number;
  down_payment_amount_discount?: number;
  down_payment_is_mercado_pago_available: boolean;
  down_payment_is_bank_transfer_available: boolean;
  down_payment_is_cash_available: boolean;
  down_payment_only_for_new_clients?: boolean;
  has_own_availability_enabled: boolean;
  online_courses_enabled: boolean;
  idea_box_enabled: boolean;
  cash_register_enabled?: boolean;
  loyalty_enabled?: boolean;
  /** Premios (v2 de Fidelización): cohorte de la beta cerrada (SQL o MCP en cyclone). */
  premios_beta_enabled?: boolean;
  /** Mercado Pago por profesional (beta). El staff hereda el del dueño. */
  mp_por_profesional_beta_enabled?: boolean;
  /** «Turnos con Mercado Pago»: apagado, las reservas de servicios no ofrecen Mercado Pago. */
  mp_for_bookings_enabled?: boolean;
  /** Staff de `venues/staff`: cobra sus turnos en su propia cuenta de MP (beta). */
  own_mercado_pago?: boolean;
  /** Referidos: vista nueva (link, crédito y referidos), beta por negocio. */
  referrals_beta_enabled?: boolean;
  offers_beta_enabled?: boolean;
  /** Reseñas verificadas: cohorte de la beta cerrada (se prende por SQL o MCP en cyclone). */
  reviews_beta_enabled?: boolean;
  catalog_products_beta_enabled?: boolean;
  community_beta_enabled?: boolean;
  /** Fichas de clientes: cohorte de la beta cerrada (se prende por SQL en cyclone). */
  client_forms_beta_enabled?: boolean;
  /** Fichaje del equipo (Equipo › Asistencia), beta por cohorte. */
  team_attendance_beta_enabled?: boolean;
  /** Módulo Finanzas: cohorte de la beta cerrada (se prende vía MCP en cyclone). */
  finanzas_beta_enabled?: boolean;
  /** Restringir reservas online de un cliente: beta por negocio (SQL o MCP en cyclone). */
  booking_restrictions_beta_enabled?: boolean;
  /** Tu mes en historias (Contenido IG): beta por negocio, en el dueño (SQL o MCP en cyclone). */
  historias_beta_enabled?: boolean;
  online_courses?: OnlineCourseI[];
  academy_product_types?: string[];
  commission_percentage?: number;
  // Comisiones diferenciadas por producto (devueltas por GET venues/staff/:id).
  product_commissions?: ProductCommissionI[];
  vendor_gallery?: VendorGalleryI;
  instagram_access_token?: string;
  instagram_user_id?: string;
}

export interface ProductCommissionI {
  product_id: number;
  commission_percentage: number | null;
}

export interface VendorNotificationI {
  id: number;
  type: VendorNotificationTypeE;
  notification_time_before_event?: number;
  vendor_id: number;
  created_at: Date;
  updated_at?: Date;
  vendor: VendorI;
}

export interface VendorActivityTrackingI {
  id: number;
  vendor_id: number;
  onboarding_finished_at?: Date;
  first_payment_method_at?: Date;
  first_service_at?: Date;
  first_label_set_at?: Date;
  first_availability_at?: Date;
  first_staff_created_at?: Date;
  working_hours_set_at?: Date;
  blocked_hours_set_at?: Date;
  first_client_booking_at?: Date;
  first_basic_storefront_at?: Date;
  first_business_booking_at?: Date;
  first_google_calendar_sync_at?: Date;
  first_mercado_pago_connection_at?: Date;
  first_frequency_set_at?: Date;
  first_side_menu_access_at?: Date;
  first_down_payment_at?: Date;
  first_business_reschedule_at?: Date;
  first_business_cancellation_at?: Date;
  first_service_duplicate_at?: Date;
  first_discount_created_at?: Date;
  first_discount_used_at?: Date;
  created_at: Date;
  updated_at?: Date;
  vendor: VendorI;
}

export interface VendorStatusHistoryI {
  id: number;
  status: VendorHistoryStatusE;
  vendor_id: number;
  created_at: Date;
  vendor: VendorI;
}

export enum VendorNotificationTypeE {
  WA_EI_FIRST_LOCAL_REMINDER = 'wa_ei_first_local_reminder',
  WA_EI_FIRST_ONLINE_REMINDER = 'wa_ei_first_online_reminder',
  WA_EI_SECOND_LOCAL_REMINDER = 'wa_ei_second_local_reminder',
  WA_EI_SECOND_ONLINE_REMINDER = 'wa_ei_second_online_reminder',
  WA_EI_CONFIRMATION = 'wa_ei_confirmation'
}

export enum VendorHistoryStatusE {
  VENUE_STAFF = 'venue_staff',
  ONBOARDING = 'onboarding',
  SETUP = 'setup',
  FREE_TRIAL = 'free_trial',
  ACTIVE = 'active',
  CHURN_REQUESTED = 'churn_requested',
  ACTIVE_AFTER_INACTIVE = 'active_after_inactive',
  ACTIVE_AFTER_FREE_TRIAL = 'active_after_free_trial',
  INACTIVE = 'inactive',
  CHURN = 'churn'
}

export enum VendorStatusE {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  DELETED = 'deleted'
}

export interface VendorWorkingHoursI {
  id: number;
  day: number;
  recurrence_pattern: string;
  working_hours: WorkingHours[];
  created_at: Date;
  updated_at?: Date;
  vendor_id: number;
  service_id: number;
  vendor: VendorI;
}

interface WorkingHours {
  start_time: string;
  end_time: string;
}

export interface ServiceVendorI {
  service_id: number;
  vendor_id: number;
  service: ServiceI;
  vendor: VendorI;
  custom_price?: number;
  custom_duration?: number;
  commission_percentage?: number;
}

export interface VenueI {
  id: number;
  vendor_owner_id: number;
  created_at: Date;
  updated_at?: Date;
  vendor_owner: VendorI;
  staff_count: number;
  vendors?: VendorI[];
  is_venue_owner_staff: boolean;
  name?: string;
  slug?: string;
  address?: string;
  latitude?: string;
  longitude?: string;
  country?: string;
  state?: string;
  neighborhood?: string;
  timezone?: string;
  phone?: string;
  status?: string;
}

export interface VenueLocationI {
  id: number;
  name?: string;
  slug?: string;
  address?: string;
  address_details?: string | null;
  latitude?: string;
  longitude?: string;
  country?: string;
  state?: string;
  neighborhood?: string;
  staff_count: number;
  service_venues?: { service_id: number }[];
}

export interface SocialMediaI {
  id: number;
  web_link?: string | null;
  instagram_link?: string | null;
  facebook_link?: string | null;
  youtube_link?: string | null;
  linked_in_link?: string | null;
}

export interface CertificationI {
  id: number;
  title: string;
  description: string;
  order: number;
}

export interface ExperienceI {
  id: number;
  title: string;
  description: string;
  order: number;
}

export interface WalletMercadoPagoI {
  id: number;
  user_id: string;
  email?: string;
  first_name?: string;
  last_name?: string;
  phone?: string;
  address?: string;
  city?: string;
  access_token: string;
  refresh_token: string;
  public_key: string;
  expires_in: number;
  needs_reconnection: boolean;
  created_at: string;
  updated_at?: string;
}

export interface EventI {
  id: number;
  service_id: number;
  days: number[] | null;
  modality: ServiceModalityE;
  duration: number;
  is_recurrent: boolean;
  recurrence_pattern?: string;
  start_at: string;
  end_at: string;
  start_time: string;
  end_time: string;
  created_at: string;
  updated_at?: string;
  service: ServiceI;
  event_instances?: EventInstanceI[];
}

export interface EventInstanceI {
  id: number;
  address?: string;
  address_details?: string;
  latitude?: string;
  longitude?: string;
  neighborhood?: string;
  country?: SupportedCountriesE;
  state?: string;
  local_slots: number;
  virtual_slots: number;
  price: number;
  participants: number;
  modality: ServiceModalityE;
  status: ScheduleStatusE;
  start_at: string;
  end_at: string;
  start_time: string;
  end_time: string;
  created_at: string;
  updated_at?: string;
  event_id: number;
  event: EventI;
  virtual_meet?: VirtualMeetI;
  buffer_after?: number;
}

export interface VirtualMeetI {
  id: number;
  meet_type: MeetTypeE;
  meet_link?: string;
  room_name?: string;
  has_started: boolean;
  started_at: Date;
  ended_at: Date;
  event_instance: EventInstanceI;
}

export interface ServiceI {
  /** Plan Car · agenda por cupo diario. NULL = la duración genera los turnos, como siempre. */
  daily_slots?: number | null;
  car_workshop_kind?:
    | 'service'
    | 'diagnostic'
    | 'accident'
    | 'installation'
    | 'delivery'
    | 'commercial_advice'
    | 'test_drive'
    | null;
  car_full_payment_discount?: number;
  /** "A convenir": el servicio se reserva sin publicar precio y el negocio cotiza después. */
  is_price_hidden?: boolean;
  is_duration_hidden?: boolean;
  /** Car: cobra por matriz de precios activa; `price` es la combinación más barata («Desde»). */
  has_price_matrix?: boolean;
  /** Minutos entre horarios ofrecidos. Independiente de la duración del servicio. */
  working_hours_start_frequency?: number | null;
  id: number;
  vendor_id: number;
  name: string;
  description: string;
  address: string;
  latitude: string;
  longitude: string;
  neighborhood: string;
  country?: SupportedCountriesE;
  state?: string;
  local_slots?: number;
  virtual_slots?: number;
  duration: number;
  modality: ServiceModalityE;
  meet_type?: MeetTypeE;
  meet_link?: string;
  price: number;
  price_usd?: number;
  /** Venta en moneda local. `price_usd > 0` habilita la venta en dólares. */
  sell_in_local?: boolean;
  rating: number;
  images_gallery?: string[];
  cover_url: string;
  status: ServiceStatusE;
  days: number[];
  booking_until_at_minutes: number;
  created_at: string;
  updated_at?: string;
  category_sub_category: CategorySubCategoryI;
  vendor?: VendorI;
  events?: EventI[];
  packs?: PackI[];
  address_details?: string;
  is_for_subscription: boolean;
  is_on_demand: boolean;
  policy_refund_mins: number;
  faqs?: FAQI[];
  multi_vendors: ServiceVendorI[];
  is_refund_policies_enabled: boolean;
  is_user_phone_required: boolean;
  assets_labels?: AssetLabelI[];
  down_payment?: DownPaymentI;
  order: number;
  slug: string;
  buffer_after?: number;
  has_own_availability: boolean;
  vendor_on_demand_hours?: VendorOnDemandHoursI[];
  is_staff_price_different?: boolean;
  is_staff_duration_different?: boolean;
  confirmation_email_content?: string;
  addons?: ServiceAddonI[];
}

export type ServiceAddonStatusE = 'ACTIVE' | 'DELETED';

export interface ServiceAddonI {
  id?: number;
  service_id?: number;
  name: string;
  description?: string;
  price: number;
  price_usd?: number;
  duration_minutes: number;
  max_quantity: number;
  position?: number;
  status?: ServiceAddonStatusE;
  created_at?: string;
  updated_at?: string;
}

export interface OrderLabelsI {
  labels: {
    id: number;
    order: number;
  }[];
  services: {
    id: number;
    order: number;
    label_id: number;
  }[];
}

export interface OrderDigitalContentLabelsI {
  labels: {
    id: number;
    order: number;
  }[];
  digital_contents: {
    id: number;
    order: number;
    label_id: number;
  }[];
}

export interface OrderOnlineCourseLabelsI {
  labels: {
    id: number;
    order: number;
  }[];
  online_courses: {
    id: number;
    order: number;
    label_id: number;
  }[];
}

export interface OrderProductLabelsI {
  labels: {
    id: number;
    order: number;
  }[];
  products: {
    id: number;
    order: number;
    label_id: number;
  }[];
}

export interface CategoryAsset {
  id: number;
  category_id: number;
  vendor_id: number;
  service_id: number;
  category: CategoryI;
  vendor: VendorI;
  service: ServiceI;
}

export interface ServiceVendorI {
  service_id: number;
  vendor_id: number;
  custom_price?: number;
  custom_duration?: number;
  commission_percentage?: number;
}

export interface CategorySubCategoryI {
  id: number;
  category: CategoryI;
  sub_category: SubCategoryI;
}

export interface ServiceSubscriptionI {
  limit: number;
  service_id: number;
  subscription_id: number;
  service: ServiceI;
  subscription?: SubscriptionI;
  is_limited: boolean;
}

export interface SubCategoryI {
  id: number;
  name: string;
}

export interface CategoryI {
  id: number;
  name: string;
}

export interface PackI {
  id: number;
  service_id: number;
  limit: number;
  price: number;
  price_usd?: number;
  discount: number;
  created_at: string;
  updated_at?: string;
  service: ServiceI;
}

// Virtual Schemas

export interface VirtualEventInstancesI {
  id: string;
  modality: ServiceModalityE;
  local_slots: number;
  virtual_slots: number;
  price: number;
  participants: number;
  status: ScheduleStatusE;
  start_at: string;
  end_at: string;
  start_time: string;
  end_time: string;
  created_at: string;
  updated_at?: string;
  event_id: number;
  virtual_meet?: VirtualMeetI;
  address?: string;
  neighborhood?: string;
  address_details?: string;
  buffer_after?: number;
  booking_event_instances?: BookingEventInstanceI[];
  event: Omit<EventI, 'event_instances'>;
}

export interface DownPaymentI {
  id: number;
  amount: number;
  amount_usd?: number;
  amount_discount: number;
  is_mercado_pago_available: boolean;
  is_bank_transfer_available: boolean;
  is_cash_available: boolean;
  created_at: Date;
  updated_at?: Date;
  vendor_id: number;
  service_id: number;
  service: ServiceI;
  vendor: VendorI;
}

export interface EventInstanceI {
  id: number;
  local_slots: number;
  virtual_slots: number;
  price: number;
  participants: number;
  modality: ServiceModalityE;
  status: ScheduleStatusE;
  start_at: string;
  end_at: string;
  start_time: string;
  end_time: string;
  created_at: string;
  updated_at?: string;
  event_id: number;
  event: EventI;
  address?: string;
  neighborhood?: string;
}
export interface SubscriptionI {
  id: number;
  payment_link: string;
  vendor_id: number;
  participants: number;
  name: string;
  description: string;
  cover_url: string;
  payment_recurrence: SubscriptionRecurrenceE;
  status: SubscriptionStatusE;
  price: number;
  price_usd?: number;
  /** Venta en moneda local. `price_usd > 0` habilita la venta en dólares. */
  sell_in_local?: boolean;
  /** Posición manual en el listado (panel y vidriera). */
  order: number;
  slots: number;
  created_at?: string;
  updated_at?: string;
  vendor?: VendorI;
  service_subscriptions?: ServiceSubscriptionI[];
  expirationDate?: string;
  faqs?: FAQI[];
}

/** Cobro registrado en el ledger de una reserva (cobros parciales o totales en mano). */
export interface PaymentCollectionI {
  date_editable?: boolean;
  id: number;
  /**
   * Con SIGNO: un `ajuste_modificacion` llega neteado (todas sus piernas sumadas) y resta
   * cuando el turno bajó de precio después de cobrado. Σ amount == `ledger_net` del summary.
   */
  amount: number;
  method: 'cash' | 'transfer' | 'mp' | string;
  category: 'cobro_parcial' | 'cobro_reserva' | 'ajuste_modificacion' | string | null;
  concept: string | null;
  created_at: string;
  created_by_vendor_id: number;
  created_by_name?: string | null;
  session_id: number | null;
  session_status: 'open' | 'closed' | string | null;
  /** Server-side: eliminable mientras el pago no esté saldado y su caja no haya cerrado. */
  deletable?: boolean;
  /**
   * Server-side: medios a los que ESTE cobro se puede corregir. Vacío/ausente = no se puede
   * (los `ajuste_modificacion` son derivados, y un descuento por medio de pago lo congela).
   * Es una lista y no un booleano para que el front nunca ofrezca algo que el server rechace.
   */
  editable_methods?: string[];
}

/** Estado cobrado/pendiente real del pago de la transacción (raíz del grupo). */
export interface CollectedSummaryI {
  sena_date_editable?: boolean;
  payment_date_editable?: boolean;
  collected: number;
  ledger_net: number;
  pending: number;
  over_collected: number;
  /** Seña configurada que el cliente aún no pagó (no descuenta del pendiente). */
  sena_esperada: number;
  /** Server-side: el medio con el que se cobró la seña se puede corregir. */
  sena_method_editable?: boolean;
  /**
   * Server-side: el MONTO cobrado de seña se puede corregir (el cliente abonó una cifra
   * distinta a la configurada). Falso con seña online —lo informa la pasarela— o con la
   * reserva ya saldada, donde el camino es eliminar el cobro y rehacerlo.
   */
  sena_amount_editable?: boolean;
  /** Techo del monto de la seña: el total de la reserva menos lo que ya cobró el ledger. */
  sena_amount_max?: number;
  /**
   * Server-side: el medio del COBRO se puede corregir. Es el caso del pago saldado que no dejó
   * fila en el ledger —de los cinco medios solo el efectivo escribe una al confirmar—, así que
   * el lápiz de la fila no llegaba a transferencia, tarjeta ni prepaga. Con fila, el server
   * manda `false` y el lápiz correcto es el de la fila: nunca hay dos para el mismo cobro.
   */
  payment_method_editable?: boolean;
}

export interface BookingI {
  hold_expires_at?: string | null;
  show_hold_countdown?: boolean;
  id: number;
  type: BookingTypeE;
  multi_booking_type?: MultiBookingTypeE;
  status: BookingStatusE;
  rescheduled_by_user_id: number;
  booked_by_user_id: number;
  rescheduled_at: Date;
  // Turno + profesional ORIGINALES (set-once en el 1er reagendamiento) — Historial del side-menu.
  original_start_at?: string | Date | null;
  original_start_time?: string | null;
  original_vendor_id?: number | null;
  original_vendor?: VendorI;
  cancelled_by_user_id: number;
  cancelled_at: Date;
  user_id: number;
  vendor_id: number;
  parent_booking_id: number | null;
  created_at: string;
  updated_at?: string;
  payments?: PaymentI[];
  /**
   * Cobrado REAL del turno: suma de los cobros CONFIRMADOS del ledger de caja.
   * Misma unidad que `payment.amount`. `0` si no hay nada cobrado.
   *
   * Opcional a propósito: es aditivo sobre el `payment` (nunca lo reemplaza) y las
   * respuestas sin el campo tienen que comportarse igual que antes de que existiera.
   * Ver `resolvePaymentBadge()`.
   */
  collected_total?: number;
  /**
   * Turno de una reserva de VARIOS turnos (lo manda el bundle de agenda): la plata es de la
   * reserva entera —el pago del padre y su cobrado—, porque el descuento, el recargo y los
   * productos viven ahí y no tienen un reparto exacto por turno. Ver `paymentSourceOf()`.
   */
  reservation?: { turnos: number; payment: PaymentI; collected_total: number };
  parent_booking?: BookingI;
  user?: UserI;
  attendee?: BookingAttendeeI;
  vendor: VendorI;
  booking_event_instance?: BookingEventInstanceI;
  booking_pack?: BookingPackI;
  booking_subscription?: BookingSubscriptionI;
  booking_unique_event?: BookingUniqueEventI;
  booking_digital_content?: BookingDigitalContentI;
  booking_online_course?: BookingOnlineCourseI;
  gift_card?: GiftCardI;
  booking_notifications?: BookingNotificationI[];
  cancelled_by_user?: UserI;
  rescheduled_by_user?: UserI;
  refund?: RefundI;
  children_bookings?: BookingI[];
  booking_addons?: BookingAddonI[];
  bookings_products?: BookingProductI[];
  /**
   * Origen de la reserva ('vendor_manual' = la creó el negocio a mano; ventas
   * directas de producto incluidas). Presente en GET bookings/:id — el listado
   * de /actividad no lo trae.
   */
  source?: string | null;
  source_metadata?: Record<string, unknown> | null;
  // Set by the backend (getBookings, agenda bundle) when the client is currently "nuevo".
  is_new_client?: boolean;
  // GET bookings/:id: la reseña de la visita (solo puntaje y visibilidad) → «Respondido» en el historial.
  review?: { id: number; rating: number; visibility: 'public' | 'private'; created_at: string } | null;
  // Cobros parciales (GET bookings/:id): ledger de cobros + estado cobrado/pendiente real.
  payment_collections?: PaymentCollectionI[];
  collected_summary?: CollectedSummaryI | null;
}

export interface BookingAttendeeI {
  booking_id: number;
  first_name: string;
  last_name: string;
  email?: string | null;
  phone?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface BookingAddonI {
  id: number;
  booking_id: number;
  service_addon_id: number;
  quantity: number;
  unit_price: number;
  unit_price_usd?: number;
  duration_minutes: number;
  created_at: string;
  service_addon?: ServiceAddonI;
}

export interface VendorPermissionsI {
  id: number;
  agenda_view: boolean;
  /** LEGADO: la API lo deriva (= bookings_create || blocked_hours_edit || on_demand_hours_edit).
   *  Solo lo lee el flag de Contenido IG; ningún otro gate. */
  agenda_edit?: boolean;
  working_hours_edit: boolean;
  /** «Agregar horarios este día» (on demand). */
  on_demand_hours_edit?: boolean;
  /** «Bloquear horarios»: crear, editar y borrar bloqueos. */
  blocked_hours_edit?: boolean;
  /** «Ingresar reserva» (simple, multi, recurrente) y clases grupales. */
  bookings_create?: boolean;
  /** Dentro del turno: registrar cobro, Asistió/Ausente, notas. */
  bookings_manage?: boolean;
  /** «Modificar reserva»: fecha, hora, duración (incluido arrastrar), servicios, productos, descuento. */
  bookings_modify?: boolean;
  /** Cancelar turno, reserva, serie, pack o plan. */
  bookings_cancel?: boolean;
  services_view: boolean;
  /** @deprecated reemplazado por clients_view / clients_view_all */
  client_data_view: boolean;
  /** Entrar a Clientes, la ficha y las notas. */
  clients_view: boolean;
  /** Contacto (email y teléfono). Ya NO implica `clients_view`. */
  clients_view_all: boolean;
  clients_edit: boolean;
  /** Nombre y apellido del cliente. Default true: si falta (payload viejo) se trata como true. */
  clients_name_view?: boolean;
  /**
   * Alcance de Analítica, no acceso: prendido = las métricas del NEGOCIO entero (todas las
   * sucursales, todo el equipo); apagado = solo los números propios del profesional.
   */
  analytics_view: boolean;
  /** Finanzas en solo lectura, con el alcance del negocio. Las escrituras son del dueño. */
  finanzas_view: boolean;
  activity_view: boolean;
  /** LEGADO: la API lo deriva (= bookings_manage || bookings_modify || bookings_cancel). No se lee. */
  activity_edit?: boolean;
  vendor: VendorI;
}

export interface BookingUniqueEventI {
  id: number;
  unique_code: string;
  quantity: number;
  redeemed_slots: number;
  has_assisted?: boolean;
  booking_id: number;
  unique_event_id: number;
  unique_event_ticket_id: number;
  unique_event_ticket_special_id: number;
  created_at: Date;
  updated_at?: Date;
  promoter_id: number;
  booking: BookingI;
  promoter: PromoterI;
  unique_event: UniqueEventI;
  unique_event_ticket: UniqueEventTicketI;
  unique_event_ticket_special: UniqueEventTicketI;
}

export interface PromoterI {
  id: number;
  name: string;
  email: string;
  phone: string;
  commission: number;
  vendor_id: number;
  vendor: VendorI[];
}

export interface BookingDigitalContentI {
  id: number;
  booking_id: number;
  digital_content_id: number;
  created_at: Date;
  updated_at?: Date;
  booking: BookingI;
  digital_content: DigitalContentI;
}

export interface BookingOnlineCourseI {
  id: number;
  booking_id: number;
  online_course_id: number;
  created_at: Date;
  updated_at?: Date;
  booking: BookingI;
  online_course: OnlineCourseI;
  course_enrollment?: CourseEnrollmentI;
}

export interface DigitalContentI {
  id: number;
  name: string;
  description: string;
  slug: string;
  cover_url: string;
  content_url: string;
  price: number;
  price_usd?: number;
  /** Venta en moneda local. `price_usd > 0` habilita la venta en dólares. */
  sell_in_local?: boolean;
  status: DigitalContentStatusE;
  images_gallery: string[];
  order: number;
  created_at: Date;
  updated_at?: Date;
  vendor_id: number;
  vendor: VendorI;
  faqs?: FAQI[];
  is_user_phone_required: boolean;
  is_transfer_fee_available: boolean;
  assets_labels?: AssetLabelI[];
}

export interface OnlineCourseI {
  id: number;
  /** Revisión de edición (sólo en el payload del dueño): la suben las ediciones por partes. */
  edit_revision?: number;
  name: string;
  subtitle?: string;
  slug: string;
  description: string;
  cover_url?: string;
  cover_video_url?: string;
  cover_video_hls_url?: string;
  cover_video_thumbnail_url?: string;
  price_local: number;
  price_usd?: number;
  /** Venta en moneda local. `price_usd > 0` habilita la venta en dólares. */
  sell_in_local?: boolean;
  status: OnlineCourseStatusE;
  course_type: OnlineCourseTypeE;
  is_user_phone_required: boolean;
  is_transfer_fee_available?: boolean;
  duration_hours?: number;
  enrolled_count: number;
  learning_objectives?: string[];
  prerequisites?: string[];
  target_audience?: string[];
  sections_order?: string[];
  access_duration_days?: number;
  created_at: Date;
  updated_at?: Date;
  vendor_id: number;
  vendor?: VendorI;
  course_modules?: CourseModuleI[];
  faqs?: FAQI[];
  assets_labels?: AssetLabelI[];
  confirmation_email_content?: string;
  testimonials?: OnlineCourseTestimonialI[];
  order?: number;
  /**
   * ¿El dueño puede bajarse los masters de las clases? Lo resuelve Cyclone (es un
   * permiso puntual, por vendor y curso): acá no se decide, solo se muestra.
   */
  can_download_videos?: boolean;
}

export interface OnlineCourseTestimonialI {
  id: number;
  online_course_id: number;
  author_name: string;
  author_title?: string;
  media_type: 'image' | 'video';
  media_url: string;
  hls_url?: string;
  thumbnail_url?: string;
  duration_seconds?: number;
  content?: string;
  order: number;
  created_at: string;
  updated_at?: string;
}

export interface OnlineCourseGalleryItemI {
  id: number;
  online_course_id: number;
  media_type: 'image' | 'video';
  media_url: string;
  hls_url?: string;
  thumbnail_url?: string;
  title?: string;
  duration_seconds?: number;
  order: number;
  created_at: string;
  updated_at?: string;
}

export interface CourseModuleI {
  id: number;
  title: string;
  order: number;
  created_at: Date;
  updated_at?: Date;
  course_id: number;
  course_classes?: CourseClassI[];
}

export interface CourseClassI {
  id: number;
  title: string;
  order: number;
  content_type?: ClassContentTypeE;
  video_source?: VideoSourceE;
  video_url?: string;
  hls_url?: string;
  duration_seconds?: number;
  content_url?: string;
  content_filename?: string;
  content_size?: number;
  rich_text_content?: string;
  thumbnail_url?: string | null;
  is_preview: boolean;
  /**
   * Solo para clases de archivo (pdf/attachment): si el alumno puede bajárselo.
   * Ausente en payloads viejos — tratar `undefined` como permitido.
   */
  allow_download?: boolean;
  // Quiz-specific settings
  record_grades?: boolean;
  instant_feedback?: boolean;
  quiz_questions?: QuizQuestionI[];
  created_at: Date;
  updated_at?: Date;
  module_id: number;
}

// Quiz interfaces
export interface QuizQuestionI {
  id?: number;
  course_class_id?: number;
  question_text: string;
  image_url?: string;
  order: number;
  answers: QuizAnswerI[];
  created_at?: Date;
  updated_at?: Date;
}

export interface QuizAnswerI {
  id?: number;
  question_id?: number;
  answer_text: string;
  is_correct: boolean;
  order: number;
  created_at?: Date;
  updated_at?: Date;
}

export interface QuizAttemptAnswerI {
  question_id: number;
  selected_answer_ids: number[];
  is_correct: boolean;
}

export interface RefundI {
  id: number;
  type: RefundTypeE;
  status: PaymentStatusE;
  amount: number;
  created_at: string;
  updated_at?: string;
  booking_id: number;
  payment_order?: PaymentOrderI;
}

export interface PaymentOrderI {
  id: number;
  checkout_id?: string;
  payment_text: string;
  transaction_amount: number;
  currency_code: string;
  reference: string;
  source_name: string;
  source_type: string;
  card_info?: CardI;
}

export interface BookingPackI {
  id: number;
  status: BookingPackStatusE;
  start_at: Date;
  end_at: Date;
  limit: number;
  price: number;
  days: number;
  discount: number;
  service_id: number;
  booking_id: number;
  created_at: Date;
  updated_at?: Date;
  service?: ServiceI;
  ticket?: TicketI;
  booking: BookingI;
}

export interface BookingSubscriptionI {
  id: number;
  status: BookingSubscriptionStatusE;
  start_at: string;
  subscription_id: number;
  booking_id: number;
  created_at: string;
  updated_at?: string;
  subscription: SubscriptionI;
  booking?: BookingI;
  tickets?: TicketI[];
  end_at: Date;
}

export interface BookingEventInstanceI {
  car_price_inputs?: { key: string; label: string; value: string | number }[] | null;
  id: number;
  /** Historical workshop membership, retained after a service changes mode. */
  workshop_owner_id?: number | null;
  modality: ServiceModalityE;
  price: number;
  has_assisted?: boolean;
  notes?: string;
  /** Plan Car: cuándo el negocio marcó el turno como "listo para retirar" (null = no marcado). */
  ready_for_pickup_at?: string | null;
  ready_for_pickup_by_vendor_id?: number | null;
  event_instance_id: number;
  booking_id: number;
  created_at: string;
  updated_at?: string;
  event_instance: EventInstanceI;
  booking?: BookingI;
}

export interface VendorOnDemandHoursI {
  id: number;
  vendor_id: number;
  service_id?: number;
  start_at: string;
  working_hours: WorkingHours[];
  service?: ServiceI;
  vendor: VendorI;
  is_unique_for_service: boolean;
}

export interface VendorOnDemandHoursMergedI {
  id: number;
  vendor_id: number;
  service_ids?: number[];
  start_at: string;
  working_hours: WorkingHours[];
  vendor: VendorI;
}

export interface AssetI {
  id: number;
  pack_id: number;
  subscription_id: number;
  user_id: number;
  status: AssetStatusE;
  start_at: string;
  end_at: string;
  user: UserI;
  pack?: PackI;
  subscription?: SubscriptionI;
  created_at: string;
  updated_at?: string;
  tickets?: TicketI[];
}

export interface PaymentI {
  id: number;
  booking_id: number;
  down_payment_id: number;
  vendor_agora_plans_invoice_id: number;
  coupon_id: number;
  payment_link: string;
  payment_link_start_at: Date;
  payment_link_end_at: Date;
  type?: PaymentTypeE;
  status: PaymentStatusE;
  currency?: CurrencyE;
  amount: number;
  agora_fee?: number;
  down_payment_amount: number;
  down_payment_type?: PaymentTypeE;
  down_payment_at?: Date;
  confirmed_at?: Date;
  /** Fecha en la que Mercado Pago libera la plata al vendor. Solo cobros por MP. */
  money_release_date?: string | Date;
  discount: number;
  discount_by_payment_method: number;
  discount_by_coupon: number;
  discount_by_vendor?: number;
  /** Aumento manual del negocio. A diferencia del descuento (que baja `amount` dejando
   *  `total_amount` de baseline), el aumento ya está sumado DENTRO de `total_amount`. */
  surcharge_by_vendor?: number;
  discount_by_dynamic_pricing?: number;
  discount_by_offer?: number;
  offer_redemptions?: Array<{
    id: number;
    offer_id: number;
    offer_name: string;
    discount_amount: number;
    price_before: number;
  }>;
  agora_comission: number;
  is_offline_payment_confirmed?: boolean;
  total_amount: number;
  gateway: WalletTypeE;
  gift_card_id?: number;
  discount_by_gift_card?: number;
  discount_by_level?: number;
  discount_by_loyalty_reward?: number;
  gift_card?: GiftCardI;
  created_at: Date;
  updated_at?: Date;
  booking?: BookingI;
  payment_order?: PaymentOrderI;
  vendor_agora_plans_invoice?: VendorAgoraPlanInvoiceI;
  down_payment?: DownPaymentI;
  coupon?: CouponI;
}

export interface TicketI {
  id: number;
  limit: number;
  service_id: number;
  asset_id: number;
  service?: ServiceI;
  asset?: AssetI;
}

export interface InvitationI {
  id: number;
  status: InvitationStatus;
  user_invitee_email: string;
  user_invitor_id: number;
  user_invitee_id: number;
  created_at: Date;
  updated_at?: Date;
  user_invitor: UserI;
  user_invitee: UserI;
}

// ── Programa de referidos (GET me/referrals) ────────────────────────────────
export type ReferralProgramTypeT = 'common' | 'ambassador' | 'creator';
export type ReferralSettlementT = 'credit' | 'transfer';
export type ReferralStatusT = 'invited' | 'incomplete' | 'trial' | 'active' | 'inactive' | 'churned';
export type ReferralCommissionStatusT = 'earned' | 'paid' | 'voided';
export type ReferralHistoryKindT =
  'payout' | 'credit_adhoc' | 'credit_applied' | 'credit_lost' | 'adjustment' | 'commission';

export interface MoneyI {
  amount: number;
  currency: string;
}

export interface ReferralBankAccountI {
  holder: string | null;
  bank: string | null;
  alias: string | null;
  account: string | null;
  document: string | null;
  currency: string | null;
}

export interface ReferralProgramI {
  enabled: boolean;
  flash_v2: boolean;
  type: ReferralProgramTypeT;
  settlement: ReferralSettlementT;
  country: string | null;
  start_date: string;
  commission: MoneyI | null;
  referred_discount: MoneyI | null;
  min_withdrawal: MoneyI;
  bank_account: ReferralBankAccountI | null;
}

export interface ReferralBalancesI {
  credit: {
    currency: string | null;
    earned: number;
    applied: number;
    reserved: number;
    lost: number;
    available: number;
  };
  transfer: {
    currency: 'USD';
    earned: number;
    unpaid: number;
    paid: number;
    missing_for_withdrawal: number;
    can_withdraw: boolean;
  };
}

export interface ReferralCountersI {
  invited: number;
  incomplete: number;
  trial: number;
  paid: number;
  active: number;
  inactive: number;
  churned: number;
}

export interface ReferralCommissionI {
  id: number;
  status: ReferralCommissionStatusT;
  amount: number;
  currency: string;
  settlement: ReferralSettlementT;
  earned_at: string;
  paid_at: string | null;
  void_reason: string | null;
}

export interface ReferralRowI {
  invitation_id: number;
  email: string;
  name: string | null;
  business_name: string | null;
  username: string | null;
  phone: string | null;
  avatar_url: string | null;
  country: string | null;
  registered_at: string;
  channel: string | null;
  status: ReferralStatusT;
  first_paid_at: string | null;
  paid: boolean;
  counts_for_program: boolean;
  commission: ReferralCommissionI | null;
}

export interface ReferralHistoryEntryI {
  id: number;
  kind: ReferralHistoryKindT;
  settlement: ReferralSettlementT;
  amount: number;
  currency: string;
  status: string;
  occurred_at: string;
  invoice_id: number | null;
  reference: string | null;
  local_amount: number | null;
  local_currency: string | null;
  note: string | null;
}

export interface ReferralsSummaryI {
  program: ReferralProgramI;
  link: string | null;
  code: string | null;
  balances: ReferralBalancesI;
  counters: ReferralCountersI;
  referrals: ReferralRowI[];
  history: ReferralHistoryEntryI[];
}

/** GET affiliates/ref/:code (público): de parte de quién viene un link corto. */
export interface PublicReferrerI {
  code: string;
  username: string | null;
  display_name: string;
  avatar_url: string | null;
  type: ReferralProgramTypeT;
  country: string | null;
  referred_discount: MoneyI | null;
}

export interface CouponI {
  id: number;
  status: CouponStatusE;
  code: string;
  name: string;
  apply_to?: CouponApplyToE;
  amount_discount?: number;
  amount_usd_discount?: number;
  discount?: number;
  quantity: number;
  total_quantity: number;
  end_at: Date;
  user_id: number;
  vendor_id: number;
  service_id?: number;
  subscription_id?: number;
  unique_event_ticket_id?: number;
  digital_content_id?: number;
  online_course_id?: number;
  applicable_assets?: { type: string; id?: number }[];
  applicable_shift_days?: number[];
  applicable_purchase_days?: number[];
  created_at: Date;
  updated_at?: Date;
  vendor: VendorI;
  subscription?: SubscriptionI;
  unique_event_ticket?: UniqueEventTicketI;
  digital_content?: DigitalContentI;
  online_course?: OnlineCourseI;
  service?: ServiceI;
  user?: UserI;
}

export interface VendorPromptsI {
  id: number;
  is_agenda_working_hours_enabled: boolean;
  created_at: Date;
  updated_at?: Date;
  vendor_id: number;
  vendor: VendorI;
}

export interface VendorAgoraPlanI {
  id: number;
  vendor_id: number;
  agora_plan_id: number;
  is_free_trial: boolean;
  is_plan_available: boolean;
  /** Crédito por referidos que se descuenta de la próxima factura (`calculated_amount` ya lo trae restado). */
  referral_credit?: number;
  event_fee_percentage?: number;
  digital_content_fee_percentage?: number;
  custom_payment_link?: string;
  payment_recurrence?: SubscriptionRecurrenceE;
  boxful_subscription_id?: number;
  boxful_subscription_status?: string;
  discount?: number;
  // Descuento porcentual ad-hoc (0-1, e.g. 1 = 100%). Aplica sobre el subtotal completo,
  // mismo criterio que el consumption-calculator de cyclone.
  discount_percentage?: number | null;
  boxful_customer_id?: string;
  stripe_subscription_status?: string;
  payment_method_last_4?: string;
  boxful_plan_id?: number;
  payment_type: AgoraSubscriptionPaymentTypeE;
  start_at: Date;
  payment_method: string;
  funding_type?: 'credit' | 'debit' | 'prepaid' | string | null;
  card_brand?: string | null;
  card_issuer?: string | null;
  card_category?: string | null;
  card_first_six?: string | null;
  payment_date?: Date;
  price_monthly: number;
  price_venue_staffs_monthly: number;
  price_wa_packs_monthly: number;
  // Monto final que cyclone realmente cobra (post-descuentos). Strings desde la DB
  // por la columna decimal, así que en cliente conviene pasarlo por Number().
  calculated_amount?: number | string | null;
  limit_payment_date?: Date;
  expired_reason?: string | null;
  churn_requested_at?: Date | null;
  // Computed flag attached by the session endpoint (cyclone session.controller.ts):
  // true while Boxful is retrying the payment or an invoice is in dunning.
  has_dunning_payment_in_process?: boolean;
  mp_subscription_id?: string;
  created_at: Date;
  updated_at?: Date;
  vendor: VendorI;
  agora_plan?: AgoraPlanI;
  vendor_agora_plan_invoices?: VendorAgoraPlanInvoiceI[];
  venue_staffs?: number;
  discount_reason?: string;
  wa_packs?: number;
  wa_packs_next_cycle?: number | null;
  wa_bonus_enabled?: boolean;
  wa_cancel_at?: string | null;
  wa_renewal_sync_pending?: boolean;
  wa_packs_monthly?: number;
  wa_pending_activation?: boolean;
  wa_pending_edit?: boolean;
  /** Config de WhatsApp gestionada por soporte: la app no permite editar ni dar de baja. */
  wa_config_locked?: boolean;
  is_wa_limit_enforced?: boolean;
  wa_pending_config?: {
    notifications?: Array<{ type: string; time_before_event: number }>;
    wa_packs: number;
    payment_instruction_id?: number;
    checkout_session_id?: string;
    /** One-time charge amount being processed (Stripe checkout / Boxful PI). */
    activation_amount?: number;
  };
  revenue_share_enabled?: boolean;
  price_revenue_share_monthly?: number;
  revenue_share_activated_at?: Date | null;
  catalog_products_enabled?: boolean;
  price_catalog_products_monthly?: number;
  catalog_products_activated_at?: Date | null;
  community_enabled?: boolean;
  price_community_monthly?: number;
  community_activated_at?: Date | null;
}

// ToDo: Add the isActive property on the AgoraPlanI object
export interface AgoraPlanI {
  id: number;
  name: AgoraPlanNameE;
  price_monthly?: number;
  created_at: Date;
  updated_at?: Date;
}

export enum VendorAgoraPlanInvoiceTypeE {
  RECURRING = 'recurring',
  ONE_TIME = 'one_time'
}

export interface VendorAgoraPlanInvoiceI {
  id: number;
  vendor_agora_plan_id: number;
  status?: VendorAgoraPlanInvoiceStatusE;
  type?: VendorAgoraPlanInvoiceTypeE;
  // Null for one_time invoices (WA pack activation/upgrade charges) — they
  // are one-shot charges with no billing period.
  plan_subscription_start_at?: Date | null;
  plan_subscription_end_at?: Date | null;
  expiration_date: Date;
  amount: number;
  /** Crédito por referidos usado en esta factura. */
  referral_credit?: number;
  /** La factura entera se pagó con crédito por referidos (mes cubierto). */
  paid_with_referral_credit?: boolean;
  created_at: Date;
  updated_at?: Date;
  vendor_agora_plan?: VendorAgoraPlanI;
  next_retry_date?: Date;
  debit_date?: Date;
  retry_attempt: number;
  mp_authorized_payment_id?: string;
  boxful_invoice_id?: number;
  venue_staffs?: number;
  wa_sent_messages?: number;
  wa_packs?: number;
  stripe_invoice_id?: string;
  stripe_hosted_invoice_url?: string;
  venue_staffs_amount?: number;
  wa_packs_amount?: number;
  rejected_message?: string;
  /** Per-attempt motives for this invoice, oldest to newest. Populated by
   *  the Boxful sync from rejected/cancelled payments; safe to assume an
   *  empty array when no rejections (default `'{}'` on the column). */
  rejected_messages_history?: string[];
  usage_description?: string;
  vendor_agora_plan_invoice_parent_id?: number;
  parent_vendor_agora_plan_invoice?: VendorAgoraPlanInvoiceI;
  children_vendor_agora_plan_invoices?: VendorAgoraPlanInvoiceI[];
  has_in_process_payment?: boolean;
}

export interface CardI {
  cardholder: {
    identification: {
      number: string;
      type: string;
    };
    name: string;
  };
  date_created: Date;
  date_last_updated: Date;
  expiration_month: number;
  expiration_year: number;
  first_six_digits: string;
  id: string;
  last_four_digits: string;
}

export interface GoogleCalendarKeyI {
  id: number;
  user_id: string;
  email?: string;
  timezone?: string;
  access_token: string;
  refresh_token: string;
  expires_in: number;
  created_at: Date;
  updated_at?: Date;
  user: UserI;
}

export interface FAQI {
  id: number;
  name: string;
  description: string;
  order: number;
  service_id?: number;
  subscription_id?: number;
  created_at: Date;
  updated_at?: Date;
  service?: ServiceI;
  subscription?: SubscriptionI;
}

export interface UniqueEventI {
  id: number;
  name: string;
  slug: string;
  description: string;
  address?: string;
  address_details?: string;
  latitude?: string;
  longitude?: string;
  neighborhood?: string;
  country?: SupportedCountriesE;
  state?: string;
  modality: ServiceModalityE;
  duration: number;
  cover_url: string;
  status: UniqueEventStatusE;
  policy_refund_mins: number;
  booking_until_at_minutes: number;
  booking_after_minutes: number;
  meet_type?: MeetTypeE;
  meet_link?: string;
  images_gallery: string[];
  created_at: Date;
  updated_at?: Date;
  vendor_id: number;
  category_sub_category_id: number;
  vendor?: VendorI;
  category_sub_category?: CategorySubCategoryI;
  unique_events_tickets: UniqueEventTicketI[];
  unique_events_schedules: UniqueEventScheduleI[];
  sold_tickets: number;
  local_slots?: number;
  virtual_slots?: number;
  participants: number;
  is_policy_available: boolean;
  is_transfer_fee_available: boolean;
  is_booking_until_at_after: boolean;
  is_hidden: boolean;
  password: string;
  is_limited_tickets: boolean;
  max_tickets_quantity: number;
  is_user_phone_required: boolean;
  faqs?: FAQI[];
  reminder_minutes?: number;
  confirmation_email_content?: string;
}

export interface UniqueEventScheduleI {
  id: number;
  unique_event_id: number;
  start_at: Date | string;
  end_at: Date | string;
  modality?: string;
  meet_link?: string;
  meet_type?: string;
  address?: string;
  address_details?: string;
  latitude?: string;
  longitude?: string;
  created_at: Date;
  updated_at?: Date;
}

export interface UniqueEventTicketI {
  id: number;
  name: string;
  price: number;
  price_usd?: number;
  /** Venta en moneda local. `price_usd > 0` habilita la venta en dólares. */
  sell_in_local?: boolean;
  slots: number;
  is_hidden: boolean;
  modality: ServiceModalityE;
  total_slots: number;
  redeemed_slots: number;
  quantity: number;
  unique_event_id: number;
  parent_ticket_id?: number;
  unique_event: UniqueEventI;
  parent_ticket?: UniqueEventTicketI;
}

export interface OfflinePaymentI {
  id: number;
  type: OfflinePaymentTypeE;
  description: string;
  discount: number;
  created_at: Date;
  updated_at?: Date;
  vendor_id: number;
  vendor: VendorI;
}

export interface AgoraThemeColorI {
  background: string | undefined;
  title: string | undefined;
  button: string | undefined;
  text: string | undefined;
  shadow: string | undefined;
}

export interface VendorStorefrontI {
  id: number;
  vendor_id: number;
  template_name: AgoraThemeE;
  cover_url?: string;
  cover_desktop_url?: string;
  /** Formato de hora del negocio ('24h' | '12h'). Refleja en Agenda + reservas. */
  hour_format?: '12h' | '24h';
  name: string;
  background_color: string;
  title_color: string;
  button_color: string;
  text_color: string;
  button_type: TypeButtonsE;
  border_radius_px: RoundedE;
  font_type: SubtitleTypeFontE;
  title_font_type: TitleTypeFontE;
  created_at: Date;
  updated_at?: Date;
  vendor?: VendorI;
  featured_video_title: string;
  featured_video_url: string;
  shadow_color?: string;
  pattern_type?: PatternE | null;
  is_banner_displayed: boolean;
  is_avatar_displayed: boolean;
  is_whatsapp_displayed?: boolean;
  is_gift_cards_displayed?: boolean;
  gift_cards_display_style?: GiftCardsDisplayStyleE;
  size_banner: SizeImageE;
  size_avatar: SizeImageE;
  // Sitebuilder Enhancement Fields
  avatar_style?: AvatarStyleE;
  avatar_border_color?: string;
  avatar_border_width?: number;
  header_effect?: HeaderEffectE;
  header_overlay_opacity?: number;
  header_overlay_color?: string;
  social_display_style?: SocialDisplayStyleE;
  social_icon_color?: string;
  template_preset_id?: string;
  template_category?: TemplateCategoryE;
  // Storefront revamp 2026 — cover focal point + auto-contrast text mode
  cover_focal_x?: number;
  cover_focal_y?: number;
  text_contrast_mode?: TextContrastModeE;
  // Animation/Creative Template Fields
  animation_enabled?: boolean;
  animation_type?: AnimationTypeE | null;
  background_type?: BackgroundTypeE | null;
  mesh_colors?: string[] | null;
  decorative_shapes?: DecorativeShapeE | null;
  noise_overlay?: NoiseOverlayE | null;
  storefront_avatar_url?: string;
}

export type GalleryLayoutE = 'grid' | 'masonry' | 'carousel';
export type GalleryItemTypeE = 'normal' | 'before_after';

export interface GalleryItemI {
  id: string;
  image_url: string;
  caption?: string;
  order: number;
  service_id?: number | null;
  type: GalleryItemTypeE;
  before_image_url?: string;
  // Video items: image_url is the poster frame; video_url is the uploaded clip
  // (R2) and hls_url the adaptive stream once transcoded.
  media_type?: 'image' | 'video';
  video_url?: string;
  hls_url?: string;
  duration_seconds?: number;
}

export interface VendorGalleryI {
  id: number;
  vendor_id: number;
  items?: GalleryItemI[];
  layout?: GalleryLayoutE;
  columns?: number;
  spacing?: number;
  border_radius?: number;
  created_at: string;
  updated_at?: string;
}

export interface VendorLinkI {
  id: number;
  name: string;
  is_agora_link: boolean;
  agora_link_type?: AgoraLinkTypeE;
  url?: string;
  order: number;
  vendor_id: number;
  created_at: Date;
  updated_at?: Date;
  vendor: VendorI;
  is_hidden: boolean;
}

export interface SocialAppI {
  id: number;
  name: string;
}

export interface VendorSocialAppI {
  id: number;
  vendor_id: number;
  social_app_id: number;
  link: string;
  order: number;
  created_at: Date;
  updated_at?: Date;
  social_app?: SocialAppI;
  vendor?: VendorI;
}

export interface VendorBlockedHoursI {
  id: number;
  start_at: string;
  start_time: string;
  note?: string;
  end_at: string;
  end_time: string;
  created_at: Date;
  updated_at?: Date;
  vendor_id: number;
  vendor: VendorI;
}

export interface ParamsRescheduleI {
  id: number;
  duration: number;
  end_at: string;
  end_time: string;
  modality: ServiceModalityE;
  start_at: string;
  start_time: string;
}

export interface FeaturedVideoI {
  title: string;
  url: string;
  error?: string;
}

export interface NextStepI {
  id: number;
  name: string;
  url?: string;
  order: number;
  plan_name: AgoraPlanNameE;
}

export interface UniqueEventsInvitations {
  first_name?: string;
  last_name?: string;
  emails: string[];
  unique_event_ticket_id: number;
  quantity: number;
}

export interface PromoterI {
  commission: number;
  email: string;
  id: number;
  name: string;
  phone: string;
  vendor_id: number;
  promoters_events: PromoterEventI[];
  booking_unique_events: BookingUniqueEventI[];
}

export interface PromoterEventI {
  id: number;
  promoter_id: number;
  unique_event_id: number;
  unique_event: UniqueEventI;
  promoter: PromoterI;
}

export interface PromoterAssigned extends PromoterI {
  is_assigned: boolean;
}

export interface BookingNotificationI {
  id: number;
  email_sent: boolean;
  type: BookingNotificationTypeE;
  mailchimp_status?: string;
  delivery_id?: string;
  schedule_id?: number;
  error_code?: string;
  error_message?: string;
  read?: boolean;
  created_at: Date;
  updated_at?: Date;
  user: UserI;
  booking: BookingI;
}

export type LabelAssetType = 'service' | 'digital_content' | 'online_course' | 'product';

export interface LabelI {
  id: number;
  title: string;
  order: number;
  asset_type: LabelAssetType;
  vendor_id: number;
  created_at: Date;
  updated_at?: Date;
  vendor: VendorI;
  assets_labels: AssetLabelI[];
}

export interface AssetLabelI {
  id: number;
  service_id: number;
  label_id: number;
  created_at: Date;
  updated_at?: Date;
  service: ServiceI;
  label: LabelI;
}

export interface CategoryI {
  id: number;
  name: string;
  level: number;
  childrens: SubCategoryI[];
}

export interface SubCategoryI {
  id: number;
  name: string;
  level: number;
}

export enum PromoterStatusE {
  ACTIVE = 'active',
  INACTIVE = 'inactive'
}

export enum CurrencyE {
  ARS = 'ars',
  USD = 'usd',
  UYU = 'uyu',
  CLP = 'clp',
  MXN = 'mxn',
  VES = 'ves',
  BOB = 'bob',
  BRL = 'brl',
  COP = 'cop',
  CRC = 'crc',
  CUP = 'cup',
  DOP = 'dop',
  GTQ = 'gtq',
  HTG = 'htg',
  HNL = 'hnl',
  NIO = 'nio',
  PYG = 'pyg',
  PEN = 'pen',
  BZD = 'bzd',
  GYD = 'gyd',
  SRD = 'srd',
  EUR = 'eur'
}

export enum AgoraThemeE {
  SOHO = 'soho',
  MIXTAPE = 'mixtape',
  CLEAN = 'clean'
}

// Animation/Creative Template Types for Storefront
export enum BackgroundTypeE {
  SOLID = 'solid',
  GRADIENT = 'gradient',
  MESH = 'mesh',
  AURORA = 'aurora'
}

export enum DecorativeShapeE {
  NONE = 'none',
  CIRCLES = 'circles',
  BLOBS = 'blobs',
  GEOMETRIC = 'geometric',
  ORBS = 'orbs'
}

export enum NoiseOverlayE {
  NONE = 'none',
  LIGHT = 'light',
  MEDIUM = 'medium',
  HEAVY = 'heavy'
}

export enum AnimationTypeE {
  LAVA = 'lava',
  WAVE = 'wave',
  PULSE = 'pulse',
  SPOTLIGHT = 'spotlight',
  MORPH = 'morph',
  AURORA = 'aurora',
  ORBIT = 'orbit',
  PLASMA = 'plasma',
  BOKEH = 'bokeh',
  SMOKE = 'smoke',
  RIPPLE = 'ripple',
  GRADIENT_SHIFT = 'gradient-shift',
  PARTICLES = 'particles',
  LIQUID = 'liquid',
  GEOMETRIC = 'geometric',
  // Revamp 2026 — curated soft animations (the builder only offers these)
  SILK = 'silk',
  AURORA_SOFT = 'aurora_soft',
  TIDE = 'tide',
  MIST = 'mist',
  HAZE = 'haze'
}

export enum TextContrastModeE {
  MANUAL = 'manual',
  AUTO = 'auto'
}

export enum UniqueEventStatusE {
  PAUSED = 'paused',
  ACTIVE = 'active',
  FINISHED = 'finished',
  DELETED = 'deleted',
  CANCELED = 'canceled'
}

export enum VendorAgoraPlanInvoiceStatusE {
  // Boxful statuses
  PENDING = 'pending',
  PAID_OUT = 'paid_out',
  UNPAID = 'unpaid',
  SCHEDULED = 'scheduled',
  RECYCLING = 'recycling',
  CANCELED = 'canceled',
  DUNNING = 'dunning',
  CHARGED_BACK = 'charged_back',
  REFUNDED = 'refunded',

  // Stripe statuses
  VOID = 'void',
  DRAFT = 'draft', // scheduled ?
  OPEN = 'open', // processing or pending
  UNCOLLECTIBLE = 'uncollectible' // unpaid
  // paid -> paid_out
}

export enum OfflinePaymentTypeE {
  CASH = 'cash',
  BANK_TRANSFER = 'bank_transfer',
  CREDIT_CARD = 'credit_card',
  DEBIT_CARD = 'debit_card',
  // Solo para DECLARAR un cobro ya hecho (agenda / caja). No es un medio configurable de la
  // vidriera: el cliente nunca elige "prepaga" al reservar, lo informa el negocio.
  PREPAID_CARD = 'prepaid_card'
}

export enum AgoraPlanNameE {
  EVENT = 'events',
  PROFESSIONAL = 'professional',
  ACADEMY = 'academy',
  OPERATOR = 'operator',
  AGORA_UP = 'agora_up',
  // Car (Módulo Taller): plan vertical plano "todo incluido". Espejo de cyclone.
  CAR = 'car'
}

export enum ScheduleStatusE {
  BOOKABLE = 'bookable',
  PAUSED = 'paused',
  CANCELED = 'canceled',
  FINISHED = 'finished',
  FULL = 'full',
  BLOCKED = 'blocked'
}

export enum BookingNotificationTypeE {
  CONFIRMATION = 'confirmation',
  CANCELLATION = 'cancellation',
  RESCHEDULE = 'reschedule',
  EMAIL_REMINDER = 'email_reminder',
  EMAIL_REVIEW_REQUEST = 'email_review_request',
  WA_EI_REVIEW_REQUEST = 'wa_ei_review_request',
  WA_LOCAL_FIRST_REMINDER = 'wa_local_first_reminder',
  WA_ONLINE_FIRST_REMINDER = 'wa_online_first_reminder',
  WA_ONLINE_SECOND_REMINDER = 'wa_online_second_reminder',
  WA_LOCAL_SECOND_REMINDER = 'wa_local_second_reminder',
  WA_DUAL_FIRST_REMINDER = 'wa_dual_first_reminder',
  WA_EI_CONFIRMATION = 'wa_ei_confirmation',
  BOOKING_SUBSCRIPTION_REMINDER = 'booking_subscription_reminder'
}

export enum ServiceModalityE {
  LOCAL = 'local',
  VIRTUAL = 'virtual',
  OPEN = 'open',
  HYBRID = 'hybrid'
}

export enum MeetTypeE {
  ZOOM = 'zoom',
  GOOGLE_MEET = 'google_meet',
  TEAMS = 'teams',
  // WhatsApp no lleva link: el profesional llama al número que el cliente carga
  // al reservar (ver `isWhatsappMeet` en @/utils/meetType).
  WHATSAPP = 'whatsapp'
}

export type BookingStatusT = 'confirmed' | 'pending' | 'canceled' | 'unpaid' | 'refunded';
export enum BookingStatusE {
  PENDING = 'pending',
  CONFIRMED = 'confirmed',
  CANCELED = 'canceled',
  UNPAID = 'unpaid',
  REFUNDED = 'refunded'
}

export enum PaymentStatusE {
  PENDING = 'pending',
  DOWN_PAYMENT_PENDING = 'down_payment_pending',
  DOWN_PAYMENT_CONFIRMED = 'down_payment_confirmed',
  CONFIRMED = 'confirmed',
  NOT_PAID = 'not_paid'
}

export enum AssetStatusE {
  ACTIVE = 'active',
  INACTIVE = 'inactive'
}

export enum SubscriptionRecurrenceE {
  MONTHLY = 'monthly'
}

export enum PaymentTypeE {
  WALLET = 'wallet',
  PACK = 'pack',
  SUBSCRIPTION = 'subscription',
  FREE_TRIAL = 'free_trial',
  FREE = 'free',
  VENDOR_INVITE = 'vendor_invite',
  CASH = 'cash',
  BANK_TRANSFER = 'bank_transfer',
  // Cobros offline con posnet en el local (confirmación manual de pago recibido).
  CREDIT_CARD = 'credit_card',
  DEBIT_CARD = 'debit_card',
  PREPAID_CARD = 'prepaid_card'
}

export enum RefundTypeE {
  PARTIAL = 'partial',
  COMPLETE = 'complete',
  TICKET = 'ticket'
}

export enum DigitalContentStatusE {
  PAUSED = 'paused',
  ACTIVE = 'active',
  DELETED = 'deleted'
}

export enum ProductStatusE {
  PAUSED = 'paused',
  ACTIVE = 'active',
  DELETED = 'deleted'
}

export enum ProductDeliveryStatusE {
  PENDING = 'pending',
  DELIVERED = 'delivered'
}

export interface ProductI {
  is_public?: boolean;
  is_publicly_visible?: boolean;
  id: number;
  vendor_id: number;
  slug: string;
  name: string;
  description?: string;
  cover_url?: string;
  images_gallery: string[];
  price: number;
  price_usd?: number;
  /** Venta en moneda local. `price_usd > 0` habilita la venta en dólares. */
  sell_in_local?: boolean;
  cost?: number | null;
  brand?: string | null;
  /** Código del producto (SKU o código de barras); no se repite dentro del negocio. */
  sku?: string | null;
  currency: string;
  stock?: number | null;
  max_per_purchase?: number | null;
  status: ProductStatusE;
  order: number;
  created_at: Date;
  updated_at?: Date;
  assets_labels?: AssetLabelI[];
}

export interface BookingProductI {
  id: number;
  booking_id: number;
  product_id: number;
  quantity: number;
  unit_price: number;
  unit_price_usd?: number;
  total: number;
  delivery_status: ProductDeliveryStatusE;
  delivered_at?: Date;
  created_at: Date;
  updated_at?: Date;
  product?: ProductI;
  booking?: BookingI;
}

export enum OnlineCourseStatusE {
  DRAFT = 'draft',
  PAUSED = 'paused',
  ACTIVE = 'active',
  DELETED = 'deleted'
}

export enum OnlineCourseTypeE {
  STANDARD = 'standard',
  WEBINAR = 'webinar'
}

export enum CourseEnrollmentStatusE {
  ENROLLED = 'enrolled',
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
  DROPPED = 'dropped'
}

export interface CourseEnrollmentI {
  id: number;
  user_id: number;
  online_course_id: number;
  booking_id?: number | null;
  status: CourseEnrollmentStatusE;
  progress_percentage: number;
  completion_date?: Date;
  last_accessed_at?: Date;
  current_module_id?: number;
  current_class_id?: number;
  access_expires_at?: Date;
  is_access_expired: boolean;
  revoked_at?: Date;
  revoked_by_user_id?: number;
  created_at: Date;
  updated_at?: Date;
  user?: UserI;
  online_course?: OnlineCourseI;
}

export enum VideoSourceE {
  UPLOAD = 'upload',
  EXTERNAL = 'external'
}

export enum ClassContentTypeE {
  VIDEO = 'video',
  PDF = 'pdf',
  ATTACHMENT = 'attachment',
  RICH_TEXT = 'rich_text',
  QUIZ = 'quiz'
}

export enum AgoraSubscriptionPaymentTypeE {
  BANK_TRANSFER = 'bank_transfer',
  MERCADO_PAGO = 'mercado_pago',
  BOXFUL = 'boxful',
  STRIPE = 'stripe'
}

export enum BookingPackStatusE {
  PENDING = 'pending',
  ACTIVE = 'active',
  FINISHED = 'finished',
  EXPIRED = 'expired'
}

export enum BookingSubscriptionStatusE {
  PENDING = 'pending',
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  EXPIRED = 'expired'
}

export enum MultiBookingTypeE {
  SINGLE = 'single',
  MULTIPLE = 'multiple'
}

export enum BookingTypeE {
  EVENT_INSTANCE = 'event_instance',
  UNIQUE_EVENT = 'unique_event',
  PACK = 'pack',
  SUBSCRIPTION = 'subscription',
  DIGITAL_CONTENT = 'digital_content',
  ONLINE_COURSE = 'online_course',
  GIFT_CARD_PURCHASE = 'gift_card_purchase',
  PRODUCT = 'product'
}

export enum ServiceStatusE {
  PAUSED = 'paused',
  ACTIVE = 'active',
  DELETED = 'deleted'
}
export enum SubscriptionStatusE {
  PAUSED = 'paused',
  ACTIVE = 'active',
  DELETED = 'deleted'
}

export enum Auth0RolesE {
  VENDOR = 'Vendor',
  CUSTOMER = 'Customer',
  ADMIN = 'Admin'
}

export enum WalletTypeE {
  MERCADO_PAGO = 'mercado_pago',
  STRIPE = 'stripe'
}

export enum InvitationStatus {
  SENT = 'sent',
  ACCEPTED = 'accepted',
  EXPIRED = 'expired'
}

export enum CouponStatusE {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  DELETED = 'deleted'
}

export enum GiftCardTemplateStatusE {
  ACTIVE = 'active',
  PAUSED = 'paused',
  DELETED = 'deleted'
}

export enum GiftCardKindE {
  FIXED_AMOUNT = 'fixed_amount',
  SPECIFIC_SERVICE = 'specific_service'
}

export enum GiftCardDesignE {
  BIRTHDAY = 'birthday',
  GENERAL = 'general',
  SPECIAL_DATE = 'special_date',
  MIDNIGHT = 'midnight',
  BOTANICAL = 'botanical',
  DUSK = 'dusk',
  MONOGRAM = 'monogram'
}

export enum GiftCardExpirationTypeE {
  NONE = 'none',
  RELATIVE = 'relative',
  ABSOLUTE = 'absolute'
}

export enum GiftCardSourceE {
  PURCHASED = 'purchased',
  GIFTED_BY_VENDOR = 'gifted_by_vendor'
}

export enum GiftCardsDisplayStyleE {
  HERO = 'hero',
  BUTTON = 'button',
  CAROUSEL = 'carousel'
}

export enum GiftCardStatusE {
  PENDING_PAYMENT = 'pending_payment',
  ACTIVE = 'active',
  FULLY_REDEEMED = 'fully_redeemed',
  EXPIRED = 'expired',
  REFUNDED = 'refunded'
}

export interface GiftCardTemplateI {
  id: number;
  vendor_id: number;
  status: GiftCardTemplateStatusE;
  kind: GiftCardKindE;
  name: string;
  description?: string;
  currency: string;
  amount: number;
  service_ids?: number[];
  /** Sólo `specific_service`: cubre el servicio entero al precio del día (sin monto). */
  covers_full_service?: boolean;
  /** Si quien la recibe ve el monto. Las de servicio completo muestran el servicio. */
  show_amount?: boolean;
  design: GiftCardDesignE;
  design_variant?: string;
  headline?: string;
  expiration_type: GiftCardExpirationTypeE;
  expiration_days?: number;
  expiration_date?: string;
  sold_count: number;
  gifted_count: number;
  created_at: string;
  updated_at?: string;
}

export interface GiftCardI {
  id: number;
  code: string;
  gift_card_template_id: number;
  template?: GiftCardTemplateI;
  vendor_id: number;
  source: GiftCardSourceE;
  status: GiftCardStatusE;
  purchaser_user_id?: number;
  purchaser_name?: string;
  purchaser_email?: string;
  purchaser_phone?: string;
  recipient_name: string;
  recipient_email?: string;
  recipient_phone?: string;
  /** Día que eligió el comprador para que le llegue; `delivered_at` vacío = todavía no salió. */
  scheduled_delivery_at?: string;
  delivered_at?: string;
  personal_message?: string;
  service_ids?: number[];
  covers_full_service?: boolean;
  covered_service_id?: number | null;
  show_amount?: boolean;
  initial_amount: number;
  balance_remaining: number;
  currency: string;
  expires_at?: string;
  activated_at?: string;
  fully_redeemed_at?: string;
  booking_id?: number;
  created_at: string;
  updated_at?: string;
  redemptions?: GiftCardRedemptionI[];
}

export interface GiftCardRedemptionI {
  id: number;
  gift_card_id: number;
  gift_card?: GiftCardI;
  booking_id: number;
  booking?: { id: number; type: string };
  payment_id: number;
  amount_applied: number;
  balance_before: number;
  balance_after: number;
  created_at: string;
}

export enum AgoraLinkTypeE {
  CUSTOM = 'custom',
  SERVICE = 'servicios',
  QUICK_BOOKING = 'reservar',
  SUBSCRIPTION = 'suscripciones',
  DIGITAL_CONTENT = 'descargables',
  EVENT = 'eventos',
  AGENDA = 'agenda',
  ABOUT = 'bio',
  YOUTUBE = 'youtube',
  MAPS = 'maps',
  ONLINE_COURSE = 'cursos',
  GOOGLE_REVIEWS = 'resenas',
  GIFT_CARD = 'regalar',
  PRODUCT = 'productos'
}

export enum SizeImageE {
  SMALL = 'small',
  BIG = 'big'
}

export enum CouponApplyToE {
  ALL = 'all',
  PACKS = 'packs',
  SERVICES = 'services',
  SUBSCRIPTIONS = 'subscriptions',
  UNIQUE_EVENTS = 'unique_events',
  DIGITAL_CONTENTS = 'digital_contents',
  ONLINE_COURSES = 'online_courses'
}

export enum SupportedCountriesE {
  ARGENTINA = 'Argentina',
  URUGUAY = 'Uruguay',
  CHILE = 'Chile',
  MEXICO = 'México',
  VENEZUELA = 'Venezuela',
  BOLIVIA = 'Bolivia',
  BRASIL = 'Brasil',
  COLOMBIA = 'Colombia',
  COSTA_RICA = 'Costa Rica',
  CUBA = 'Cuba',
  REPUBLICA_DOMINICANA = 'República Dominicana',
  ECUADOR = 'Ecuador',
  EL_SALVADOR = 'El Salvador',
  GUATEMALA = 'Guatemala',
  HAITI = 'Haití',
  HONDURAS = 'Honduras',
  NICARAGUA = 'Nicaragua',
  PANAMA = 'Panamá',
  PARAGUAY = 'Paraguay',
  PERU = 'Perú',
  BELICE = 'Belice',
  GUYANA = 'Guyana',
  SURINAM = 'Surinam',
  PUERTO_RICO = 'Puerto Rico',
  GUAYANA_FRANCESA = 'Guayana Francesa',
  GUADALUPE = 'Guadalupe',
  MARTINICA = 'Martinica'
}
